import { useMemo } from 'react'

import { DateTime } from 'luxon'

import {
  digObject,
  formatDate,
  matchFilterBetweenDates,
  matchFilterNumber,
  matchFilterString,
  sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { getAudience } from '@functions/notification'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { NotificationRequestOptions } from '@models/types'

import type { ModuleState } from '@redux/modules/module'

const watchEntityKeys = ['notifications']

const today = DateTime.now().startOf('day')

type NotificationsFilters = {
  active?: 'true' | 'false',
  audience?: 'global' | 'agencies' | 'brands' | 'providers',
  created_date_start?: string,
  limit?: number,
  location?: 'targeted' | 'list',
  notifiable_id?: number,
  notifiable_type?: string,
  realhub_platform?: 'legacy' | 'standalone_v1',
  string?: string,
  targetLocation?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: NotificationsFilters,
  performHttpRequests?: boolean,
  requestOptions?: NotificationRequestOptions,
}

function useNotifications(options: UseEntitiesOptions = {}) {
  const { filters, requestOptions } = options
  const {
    active: filterIsActive,
    created_date_start: filterCreatedDateStart,
    audience: filterAudience,
    limit: filterLimit,
    location: filterLocation,
    notifiable_id: filterNotifiableId,
    notifiable_type: filterNotifiableType,
    realhub_platform: filterRealhubPlatform,
    string: filterString,
    targetLocation: filteredTargetLocation,
  } = filters || {}

  const {
    updatedEntities: { notifications: notificationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const notifications = useSelector(reduxState => reduxState.entities.notifications)

  const filteredNotifications = useMemo(() => {
    const filtered = Object.values(notifications).filter((notification) => {
      const {
        audience,
        end_date,
        id,
        notifiable_id,
        notifiable_type,
        options: notificationOptions,
        title,
      } = notification

      const { location, realhub_platform } = notificationOptions || {}

      const createdDate = digObject(notification, 'dates.created.date_time', '')

      const matchActive = filterIsActive === 'true' ? !end_date || DateTime.fromISO(end_date) >= today : true
      const matchInactive = filterIsActive === 'false' ? !!end_date && DateTime.fromISO(end_date) < today : true

      const formatCreatedDate = formatDate(createdDate, 'dd/MM/yyyy HH:mm', 'yyyy-MM-dd')
      const matchCreatedAt = matchFilterBetweenDates(formatCreatedDate, filterCreatedDateStart)

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchAudience = filterAudience ? filterAudience === (audience || getAudience(notifiable_type!)) : true

      const matchLocationList = filterLocation === 'list' ? !location : true
      const matchLocationTargeted = filterLocation === 'targeted' ? !!location : true

      const matchNotifiableId = matchFilterNumber(notifiable_id, filterNotifiableId)
      const matchNotifiableType = filterNotifiableType ? notifiable_type === filterNotifiableType : true

      const matchRealhubPlatform = !realhub_platform
        || matchFilterString(realhub_platform, filterRealhubPlatform, { exact: true })

      const matchNotifiable = filterNotifiableType && filterNotifiableId
        ? matchNotifiableId && matchNotifiableType
        : true
      const matchTargetLocation = filteredTargetLocation ? location === filteredTargetLocation : true

      return matchActive
      && matchAudience
      && matchCreatedAt
      && matchInactive
      && matchLocationList
      && matchLocationTargeted
      && matchNotifiable
      && matchRealhubPlatform
      && matchString
      && matchTargetLocation
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [notificationsUpdatedAt, JSON.stringify(filters)])

  const filteredNotificationsCount = filteredNotifications.length
  const hasFilteredNotifications = !!filteredNotificationsCount

  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredNotificationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // We don't want to trigger another load with targetLocation changes
  // @ts-expect-error TS2339
  const { targetLocation, ...restFilters } = filtersWithOffset

  const { loading: loadingNotifications } = useReduxAction(
    'notifications',
    'loadNotifications',
    {
      ...requestOptions,
      ...restFilters,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredNotifications,
    filteredNotificationsCount,
    hasFilteredNotifications,
    loading: loadingNotifications,
  }
}

export default useNotifications
